import { useState } from "react";

import { Button, Dialog, Table } from "@fronterahealth/frontera-ui-components";
import { RadioGroup, RadioGroupItem } from "@fronterahealth/frontera-ui-components/ui/radio-group";
import { Textarea } from "@fronterahealth/frontera-ui-components/ui/textarea";

import { parseJson } from "@components/FieldComponent/utils";
import { ReportFieldInputBaseProps } from "@components/ReportField/types";

interface SpecifierOption {
  id: string;
  label: string;
  isChecked: boolean;
}

interface SpecifierItem {
  id: string;
  category: string;
  specifier: string;
  options: SpecifierOption[];
  note: string | null;
}

const getDefaultSpecifiersData = (): SpecifierItem[] => [
  {
    id: "intellectual_impairment",
    category: "Intellectual Impairment",
    specifier: "Intellectual Impairment",
    options: [
      { id: "with", label: "With", isChecked: false },
      { id: "without", label: "Without", isChecked: false },
      { id: "deferred", label: "Deferred", isChecked: false },
    ],
    note: null,
  },
  {
    id: "language_impairment",
    category: "Language Impairment",
    specifier: "Language Impairment",
    options: [
      { id: "with", label: "With", isChecked: false },
      { id: "without", label: "Without", isChecked: false },
      { id: "deferred", label: "Deferred", isChecked: false },
    ],
    note: null,
  },
  {
    id: "medical_condition",
    category: "Medical/Genetic Condition",
    specifier: "Known medical or genetic condition or environmental factor (or specify)",
    options: [
      { id: "associated", label: "Associated", isChecked: false },
      { id: "not_associated", label: "Not Associated", isChecked: false },
    ],
    note: null,
  },
  {
    id: "neurodevelopmental_disorder",
    category: "Neurodevelopmental Disorder",
    specifier: "Another neurodevelopmental, mental, or behavioral disorder (or specify e.g., ADHD)",
    options: [
      { id: "associated", label: "Associated", isChecked: false },
      { id: "not_associated", label: "Not Associated", isChecked: false },
    ],
    note: null,
  },
  {
    id: "catatonia",
    category: "Catatonia",
    specifier: "Catatonia",
    options: [{ id: "with", label: "With", isChecked: false }],
    note: null,
  },
  {
    id: "wandering",
    category: "Wandering",
    specifier: "Wandering associated with autism spectrum disorder",
    options: [{ id: "with", label: "With", isChecked: false }],
    note: null,
  },
];

// Merges saved data onto the fixed default rows/options so a value written before an
// option was added (or missing a row entirely) still renders every current row/option.
const getRows = (value: string | null | undefined): SpecifierItem[] => {
  const parsedValue = parseJson<SpecifierItem[]>(value);
  const savedRows = Array.isArray(parsedValue) ? parsedValue : [];
  return getDefaultSpecifiersData().map((defaultSpecifier) => {
    const savedSpecifier = savedRows.find((row) => row.id === defaultSpecifier.id);
    if (!savedSpecifier) return defaultSpecifier;
    return {
      ...defaultSpecifier,
      ...savedSpecifier,
      options: defaultSpecifier.options.map((defaultOption) => {
        const savedOption = savedSpecifier.options?.find((o) => o.id === defaultOption.id);
        return savedOption ? { ...defaultOption, ...savedOption } : defaultOption;
      }),
    };
  });
};

export const ReportFieldSpecifiersTable: React.FC<ReportFieldInputBaseProps> = ({ value, onChange, disabled }) => {
  const [isNoteDialogOpen, setIsNoteDialogOpen] = useState(false);
  const [selectedSpecifierId, setSelectedSpecifierId] = useState<string | null>(null);
  const [specifierNote, setSpecifierNote] = useState<string | null>(null);

  const rows = getRows(value);
  const selectedSpecifier = rows.find((row) => row.id === selectedSpecifierId) ?? null;

  const saveRows = (updatedRows: SpecifierItem[]) => onChange(JSON.stringify(updatedRows));

  const handleOptionChange = (specifierId: string, optionId: string) => {
    saveRows(
      rows.map((specifier) => {
        if (specifier.id !== specifierId) return specifier;
        return {
          ...specifier,
          options: specifier.options.map((option) => ({ ...option, isChecked: option.id === optionId })),
        };
      }),
    );
  };

  const handleSaveNote = async () => {
    if (!selectedSpecifier) return;

    await saveRows(
      rows.map((specifier) =>
        specifier.id === selectedSpecifier.id ? { ...specifier, note: specifierNote } : specifier,
      ),
    );
    setIsNoteDialogOpen(false);
  };

  return (
    <>
      {selectedSpecifier && (
        <Dialog
          open={isNoteDialogOpen}
          setOpen={setIsNoteDialogOpen}
          title={`Note for ${selectedSpecifier.category}`}
          primaryButton={{
            text: "Save Note",
            buttonAction: "regular",
            onClick: handleSaveNote,
            className: "px-6",
          }}
          secondaryButton={{
            text: "Close",
            onClick: () => setIsNoteDialogOpen(false),
          }}
        >
          <Textarea
            aria-label="Note"
            autoFocus
            value={specifierNote ?? ""}
            onChange={(event) => setSpecifierNote(event.target.value)}
          />
        </Dialog>
      )}
      <Table
        dataTestId="specifiers-table"
        defaultItemsPerPage={20}
        cellClassName="cursor-default"
        data={rows}
        columns={[
          {
            dataIndex: "options",
            key: "status",
            title: "Status",
            className: "border-r-2 align-top w-48",
            render: (_: string | SpecifierOption[] | null, record: SpecifierItem) => {
              const checkedOption = record.options.find((option) => option.isChecked);
              return (
                <RadioGroup
                  value={checkedOption?.id}
                  onValueChange={(next) => handleOptionChange(record.id, String(next))}
                  className="space-y-2"
                >
                  {record.options.map((option) => (
                    <div key={option.id} className="flex items-center gap-3">
                      <RadioGroupItem
                        id={`${record.id}-${option.id}`}
                        value={option.id}
                        disabled={disabled}
                        aria-label={option.label}
                      />
                      <label htmlFor={`${record.id}-${option.id}`} className="text-sm text-gray-700">
                        {option.label}
                      </label>
                    </div>
                  ))}
                </RadioGroup>
              );
            },
          },
          {
            dataIndex: "specifier",
            key: "specifier",
            title: "Specifiers",
            render: (_, record: SpecifierItem) => (
              <div className="w-full">
                <div className="flex justify-between items-center">
                  <span className="text-text-secondary flex-1 py-1">{record.specifier}</span>
                  {!disabled && (
                    <Button
                      text={`${record.note?.length ? "Edit" : "Add"} Note`}
                      appearance="text"
                      buttonAction="regular"
                      onClick={() => {
                        setSelectedSpecifierId(record.id);
                        setSpecifierNote(record.note);
                        setIsNoteDialogOpen(true);
                      }}
                      className="ml-4 whitespace-nowrap shrink-0"
                    />
                  )}
                </div>
                {Boolean(record.note?.length) && (
                  <div className="mt-2">
                    <div className="bg-limestone-50 border border-limestone-200 p-2 rounded-md">{record.note}</div>
                  </div>
                )}
              </div>
            ),
          },
        ]}
      />
    </>
  );
};
